import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Client, WithFinalPricing, WithSmartPricing } from '../types/menu';
import { MenuResponse, loadMenu } from '../services/api/MenuService';
import collect from 'collect.js';
import useOngoingOrder from '../hooks/context/useOngoingOrder';
import useStore from '../hooks/context/useStore';
import { BaseComponentProps } from '../types';

export type MenuContextType = {
  loadingMenu: boolean;
  products: Client.Product[];
  promotedProducts: Client.Product[];
  categories: Client.Category[];
  modifiers: Client.Modifier[];
  modifierGroups: Client.ModifierGroup[];
};

export const MenuContext = React.createContext<MenuContextType>(null);

export default function MenuProvider({ children }: BaseComponentProps) {
  const { storeId, storeData } = useStore();
  const { orderData } = useOngoingOrder();

  const [data, setData] = useState<MenuResponse>(null);
  const [loading, setLoading] = useState(false);

  const _loadMenu = useCallback((storeId: string) => {
    setLoading(true);

    return loadMenu(storeId)
      .then((response) => setData(response.data))
      .catch((error) => console.error({ error }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    storeId ? _loadMenu(storeId) : setData(null);
  }, [storeId]);

  const applyOrderMethodPrices = useCallback<
    <K extends WithSmartPricing>(item: K) => WithFinalPricing<K>
  >(
    (item) => ({
      ...item,
      price: item.actual_order_method_prices?.[orderData.method] ?? item.base_price,
    }),
    [orderData.method],
  );

  const modifiers = useMemo<Client.Modifier[]>(
    () =>
      collect(data?.modifiers)
        .map((item) => applyOrderMethodPrices(item))
        .all(),
    [data?.modifiers, orderData.method],
  );

  const modifierGroups = useMemo<Client.ModifierGroup[]>(
    () =>
      collect(data?.modifier_groups)
        .map((item) => ({
          ...item,
          modifiers: item.modifier_ids
            .map(({ id }) => modifiers[id])
            .filter((item) => Boolean(item)),
        }))
        .all(),
    [data?.modifier_groups, modifiers, orderData.method],
  );

  const products = useMemo<Client.Product[]>(
    () =>
      collect(data?.products)
        .map((item) => ({
          ...applyOrderMethodPrices(item),
          modifier_groups: item.modifier_group_ids
            .map((item) => modifierGroups[item])
            .filter((item) => Boolean(item)),
        }))
        .all(),
    [data?.products, modifierGroups],
  );

  const categories = useMemo<Client.Category[]>(
    () =>
      collect(data?.categories)
        .map((item) => ({
          ...item,
          products: item.product_ids.map((id) => products[id]).filter((item) => Boolean(item)),
        }))
        .values()
        .filter((item: Client.Category) => item.products.length > 0)
        .sortBy('sorting_order')
        .toArray(),
    [data?.categories, products, orderData.method],
  );

  const promotedProducts = useMemo<Client.Product[]>(
    () =>
      collect(storeData.promoted_products)
        .map((item) => products[item] ?? null)
        .whereNotNull()
        .all(),
    [storeData.promoted_products, products],
  );

  const context = useMemo(
    () => ({
      loadingMenu: loading,
      products,
      promotedProducts,
      categories,
      modifiers,
      modifierGroups,
    }),
    [loading, products, promotedProducts, categories, modifiers, modifierGroups],
  );

  return <MenuContext.Provider value={context}>{children}</MenuContext.Provider>;
}
